import { useTranslate } from '@va/localization';
import {
  createInitialScrollState,
  InfiniteScrollFetcher,
  InfiniteScrollPagination,
  useInfiniteScroll,
  useOutsideClick,
} from '@va/util/hooks';
import { useAriaListboxPattern } from '@va/util/misc';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { SWRResponse } from 'swr';
import { fontWeights, ParagraphWithTooltip } from '../../typography';
import { TextInput, TextInputProps } from '../text-input';

export type AutocompleteOptionsFetcher = (
  search: string,
  shouldFetch: boolean,
  pagination: InfiniteScrollPagination,
  controller: AbortController,
) => SWRResponse<{ items: { id: string; value: string }[]; total: number }>;

type AutocompleteInputProps = {
  suggestions: Array<string> | AutocompleteOptionsFetcher | undefined;
  onSuggestionClick: (suggestion: string) => void;
  loading?: boolean;
  className?: string;
} & TextInputProps;

export function AutocompleteInput(props: AutocompleteInputProps) {
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const { suggestions: suggestionsProp = [], loading, bgColor, disabled, value, className } = props;
  const translate = useTranslate();
  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const showSuggestionsOnFocus = useRef(true);

  const suggestions = useMemo(
    () => (Array.isArray(suggestionsProp) ? suggestionsProp.map((s) => ({ id: s, value: s })) : suggestionsProp),
    [suggestionsProp],
  );
  const shouldFetchSuggestions = useMemo(() => suggestions && typeof suggestions === 'function', [suggestions]);

  const fetcher = useMemo<InfiniteScrollFetcher<{ id: string; value: string }>>(() => {
    if (Array.isArray(suggestions)) {
      return (() => ({
        data: { total: 1, items: suggestions },
        mutate: async () => ({ total: 1, items: suggestions }),
        isLoading: false,
        isValidating: false,
        error: undefined,
      })) satisfies InfiniteScrollFetcher<{ id: string; value: string }>;
    }

    return (shouldFetch: boolean, pagination: InfiniteScrollPagination, _, controller: AbortController) =>
      suggestions(value?.toString() ?? '', shouldFetch, pagination, controller);
  }, [value, suggestions]);

  const { isFetchingInitial, items, lastElementRef, resetAndRefetch } = useInfiniteScroll({
    fetchHook: fetcher,
    initialState: createInitialScrollState<{ id: string; value: string }>(),
    dependencies: [value],
    preventFetch: !shouldFetchSuggestions,
  });

  const suggestionItems = useMemo(() => (Array.isArray(suggestions) ? suggestions : items), [items, suggestions]);

  useEffect(() => {
    resetAndRefetch();
  }, [value, resetAndRefetch]);

  useOutsideClick(ref, () => {
    setSuggestionsOpen(false);
  });

  const onSuggestionClick = useCallback(
    (suggestion: string) => {
      props.onSuggestionClick(suggestion);
      setSuggestionsOpen(false);
      showSuggestionsOnFocus.current = false;
      inputRef.current?.focus();
    },
    [props],
  );

  const { listboxProps } = useAriaListboxPattern<HTMLDivElement>({
    id: 'autocomplete-input',
    enabled: suggestionsOpen,
  });

  return (
    <div
      ref={ref}
      className={classNames('rounded-15', className)}
      style={{
        background: disabled ? '' : bgColor,
      }}
    >
      <TextInput
        ref={inputRef}
        {...props}
        onChange={(e) => {
          setSuggestionsOpen(true);
          props.onChange(e);
        }}
        clearField={() => {
          props.clearField();
          setSuggestionsOpen(true);
        }}
        onFocus={() => {
          if (showSuggestionsOnFocus.current) setSuggestionsOpen(true);
          showSuggestionsOnFocus.current = true;
        }}
      />
      {suggestionsOpen && (
        <div className={'max-h-144px overflow-y-auto scrollbar scrollbar-thumb'} {...listboxProps}>
          {(loading || isFetchingInitial) && <Skeleton count={3} className={'h-48'}></Skeleton>}
          {suggestionItems?.length === 0 && (
            <div className={'text-15 py-12px px-18px rounded-15 text-gray-charcoal font-medium h-48'}>
              {translate('all.autocompleteDropdown.noResults')}
            </div>
          )}
          {suggestionItems?.map((suggestion, index, arr) => {
            if (index === arr.length - 1) {
              return (
                <SuggestionItem key={index} value={suggestion.value} onClick={onSuggestionClick} ref={lastElementRef} />
              );
            }

            return <SuggestionItem key={index} value={suggestion.value} onClick={onSuggestionClick} />;
          })}
        </div>
      )}
    </div>
  );
}

type SuggestionItemProps = {
  value: string;
  onClick: (value: string) => void;
};

const SuggestionItem = forwardRef<HTMLDivElement, SuggestionItemProps>(({ onClick, value }, ref) => {
  return (
    <ParagraphWithTooltip
      id={value}
      role='option'
      aria-selected={false}
      ref={ref}
      className={'cursor-pointer text-15 py-12px px-18px rounded-15 h-48'}
      colorClassName='text-gray-charcoal hover:bg-gray-concrete'
      weight={fontWeights.medium}
      onClick={() => {
        onClick(value);
      }}
    >
      {value}
    </ParagraphWithTooltip>
  );
});
